// hooks/useConversationListLogic.js
import { useEffect, useState, useRef, useMemo } from "react";
import { useAuthContext } from "../context/AuthContext";

const STATUS_LABELS = {
  0: "Pendente",
  1: "Em Atendimento",
  2: "Concluída",
  3: "Contato Futuro",
  4: "Perdida",
  5: "Cancelada",
};
const getStatusLabel = (status) => {
  if (status == null || status === "") return null;
  const n = Number(status);
  return Number.isFinite(n) && n >= 0 && n <= 5 ? STATUS_LABELS[n] : null;
};

export const useConversationListLogic = ({
  conversations,
  selectedConversation,          // (mantido para futuras dependências)
  onSelectConversation,
  refetchConversations,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,                    // (mantido para futuras dependências/UX)
  initialLoading,                // (mantido para futuras dependências/UX)
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
}) => {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [selectedForContextMenu, setSelectedForContextMenu] = useState(null);

  const ownershipFilters = ["Minhas Solicitações", "Outros Usuários", "Fila", "ChatBots", "Agentes de IA"];
  const additionalFilters = [
    "Não Lidas",
    "Pendente",
    "Em Atendimento",
    "Contato Futuro",
    "Concluída",
    "Perdida",
    "Cancelada",
  ];

  /** Preset "Meu dia": foco do atendente ao logar */
  const MEU_DIA_FILTERS = ["Minhas Solicitações", "Pendente", "Em Atendimento"];
  const isMeuDiaActive =
    Array.isArray(filters) &&
    filters.length === MEU_DIA_FILTERS.length &&
    MEU_DIA_FILTERS.every((f) => filters.includes(f));
  const setMeuDia = () => setFilters(MEU_DIA_FILTERS);

  const bottomRef = useRef(null);

  const listRef = useRef();

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchTerm, filters]);


  const { userData } = useAuthContext();

  const filteredConversations = useMemo(() => {
    const list = Array.isArray(conversations) ? conversations : [];
    const activeFilters = Array.isArray(filters) ? filters : [];

    const hasOwnershipFilter = activeFilters.some((f) =>
      ownershipFilters.includes(f)
    );
    const hasAdditionalFilter = activeFilters.some((f) =>
      additionalFilters.includes(f)
    );

    /* ===================== Normalização, filtro e ORDEM ===================== */
    const normalizeTs = (ts) => {
      if (typeof ts === "number") return Number.isFinite(ts) ? ts : 0;
      const d = new Date(ts);
      return Number.isFinite(d.getTime()) ? d.getTime() : 0;
    };
    
    const filtered = list
      .map((c) => ({
        ...c,
        unread: Number(c?.unread ?? 0),
        unreadClient: Number(c?.unreadClient ?? 0),
        _ts: normalizeTs(c?.timestamp),
      }))
      .filter((c) => {
        // Ownership (OR: qualquer um dos selecionados)
        const hasUser = c.userId != null && c.userId !== "" && c.userId !== 0 && String(c.userId) !== "0";
        const hasAgent = c.agentId != null && c.agentId !== "" && c.agentId !== 0 && String(c.agentId) !== "0";
        const userUra = Number(c.userUra) || 0;

        const isAssignedToMe =
          hasUser &&
          (c.assignedTo === userData?.nome || String(c.assignedTo || "").trim() === String(userData?.nome || "").trim());
        // Fila = sem atribuição a usuário nem agente (fila “estrita”; combina com outros chips Quem por OR)
        const isStrictQueue = !hasUser && !hasAgent;
        const isAssignedToOther =
          hasUser && !hasAgent &&
          c.assignedTo !== userData?.nome && String(c.assignedTo || "").trim() !== String(userData?.nome || "").trim();
        const isAssignedToAgent = hasAgent;
        const isAssignedToChatBot = hasUser && userUra === 1;

        const matchesOwnership =
          !hasOwnershipFilter ||
          (activeFilters.includes("Minhas Solicitações") && isAssignedToMe) ||
          (activeFilters.includes("Fila") && isStrictQueue) ||
          (activeFilters.includes("Outros Usuários") && isAssignedToOther) ||
          (activeFilters.includes("ChatBots") && isAssignedToChatBot) ||
          (activeFilters.includes("Agentes de IA") && isAssignedToAgent);

        // Adicionais (OR: status ou não lidas)
        const statusLabel = c.statusLabel ?? getStatusLabel(c.status);
        const matchesAdditional =
          !hasAdditionalFilter ||
          (activeFilters.includes("Não Lidas") && c.unreadClient > 0) ||
          (statusLabel != null && activeFilters.includes(statusLabel));

        // Busca (por nome; pode expandir para lastMessage se quiser)
        const q = (searchTerm || "").toLowerCase();
        const matchesSearch = (c.name || "").toLowerCase().includes(q);

        return matchesOwnership && matchesAdditional && matchesSearch;
      });

    // 📌 ORDEM: mais recentes em CIMA (timestamp desc) — igual WhatsApp
    // Tie-break por id desc para estabilidade entre páginas.
    return filtered
      .sort((a, b) => b._ts - a._ts || (Number(b.id) - Number(a.id)))
      .slice();
  }, [conversations, filters, searchTerm, userData]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const el = bottomRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      {
        root: el.closest(".sidebar-scroll") || null,
        threshold: 0.1,
      }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [filteredConversations.length, hasNextPage, isFetchingNextPage]);

  /* ===================== Handlers básicos ===================== */
  const handleSearchChange = (event) => {
    setSearchTerm(event?.target?.value || "");
  };

  const handleFilterClick = (event) => setFilterAnchor(event.currentTarget);
  const handleFilterClose = () => setFilterAnchor(null);

  const toggleFilter = (filter) => {
    setFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  };

  const sanitizeText = (htmlString) => {
    if (!htmlString) return "—";
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    const text = div.textContent || div.innerText || "";
    return text.trim() || "—";
    // (apenas para preview; render seguro do conteúdo deve ser feito no item)
  };

  /* ===================== Scroll / paginação ===================== */
  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const threshold = el.scrollHeight - el.clientHeight - 50;

    // refresh suave ao "puxar" para cima (opcional)
    if (scrollTop < 0 && typeof refetchConversations === "function") {
      refetchConversations();
    }

    // paginação infinita (carrega próxima página quando chega ao final)
    if (
      el.scrollTop >= threshold &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  /* ===================== Context menu / popup ===================== */
  const handleOpenInPopup = (conversation) => {
    if (!conversation?.id) return; // guard
    const width = window.screen.width;
    const height = window.screen.height;

    window.open(
      `/popup-call/${conversation.id}`,
      "ConversationPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  const handleContextMenu = (event, conversation) => {
    event.preventDefault();
    setSelectedForContextMenu(conversation || null);
    setContextMenuAnchor({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(null);
    setSelectedForContextMenu(null);
  };

  /* ===================== Click em item ===================== */
  const handleItemClick = (event, conversation) => {
    if (!conversation) return; // guard
    if (event?.ctrlKey) {
      handleOpenInPopup(conversation);
    } else if (typeof onSelectConversation === "function") {
      onSelectConversation(conversation);
    }
  };

  /* ===================== Exports ===================== */
  return {
    listRef,
    bottomRef,
    filterAnchor,
    contextMenuAnchor,
    selectedForContextMenu,
    ownershipFilters,
    additionalFilters,
    isMeuDiaActive,
    setMeuDia,
    handleSearchChange,
    handleFilterClick,
    handleFilterClose,
    toggleFilter,
    sanitizeText,
    handleScroll,
    handleOpenInPopup,
    handleContextMenu,
    handleContextMenuClose,
    handleItemClick,
    filteredConversations,
  };

};
